import * as THREE from 'three';
import {
    CabinetMesh,
    Faces,
    RoomPlannerProduct,
} from 'components/customer/RoomPlanner/types.d';
import {
    getDirection,
    getGroupWidth,
    hasAdjustableLegs,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {getFacingRotation} from 'components/customer/RoomPlanner/helpers/snapping/placeholderSnapping';
import {
    isCornerLShapedCabinet,
    isNonRectShapeProduct,
} from 'components/customer/RoomPlanner/helpers/shapeProducts';
import {
    PairFacts,
    ProductFacts,
    SnapContext,
    SnapDir,
    SnapDirValue,
    ComputeSnapContextInput,
    ComputeSnapContextResult,
    ComputeCabinetSnapMetaInput,
    ComputeCabinetSnapMetaResult,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

export interface SnapOptions {
    camera: THREE.Camera;
    overlappingCabinets?: CabinetMesh[] | null;
    wallGroup?: THREE.Object3D | null;
}

/**
 * The context every snapping algorithm is handed for one drag.
 * @param {THREE.Group} cabinetGroup The dragged product.
 * @param {THREE.Vector3} targetPos Where the drag puts it.
 * @param {THREE.Group[]} productsRef Every product in the room.
 * @param {SnapOptions} options The camera, overlaps and walls.
 * @return {SnapContext} The context.
 */
export const createSnapContext = (
    cabinetGroup: THREE.Group,
    targetPos: THREE.Vector3,
    productsRef: THREE.Group[],
    {camera, overlappingCabinets = null, wallGroup}: SnapOptions
): SnapContext => {
    const currentPos = new THREE.Vector3();
    cabinetGroup.getWorldPosition(currentPos);

    const {
        width: latestWidth,
        depth: latestDepth,
        height: latestHeight,
    } = getGroupWidth(cabinetGroup);

    return {
        targetPos,
        currentPos,
        productsRef,
        overlappingCabinets,
        camera,
        wallGroup,
        latestWidth,
        latestDepth,
        latestHeight,
    };
};

/**
 * What routing and the strategies read about one product, worked out once.
 * @param {THREE.Group} group The product.
 * @return {ProductFacts} Its facts at its current position.
 */
export const describeProduct = (group: THREE.Group): ProductFacts => {
    const {userData} = group;
    const box = new THREE.Box3().setFromObject(group);

    return {
        group,
        box,
        height: box.max.y - box.min.y,
        direction: getDirection(getFacingRotation(group)),
        isUpper: Boolean(userData.isUpperProduct),
        isBulkhead: Boolean(userData.isBulkheadComponent),
        isFloorProduct: Boolean(userData.isFloorProduct),
        isKickLength: Boolean(userData.isKickerLengthProduct),
        isNonManualPanel: Boolean(userData.isNonManualPanel),
        isFiller: Boolean(
            userData.isManualPanel ||
            userData.isPanelProduct ||
            userData.isVerminProduct ||
            userData.isFillerProduct
        ),
        sitsOnFloor: Boolean(userData.sitOnFloor),
        // Measured only when a row asks: it builds a box per leg, and only
        // kicker frame drags need it.
        get hasLegs() {
            return hasAdjustableLegs(group);
        },
        isCornerLShape: isCornerLShapedCabinet(group),
        isNonRectShape: isNonRectShapeProduct(group),
        isReturn: Boolean(userData.isReturnProduct),
        isRightReturn: Boolean(userData.isRightReturnProduct),
        ignoresSnapping: Boolean(userData.ignoreSnapping),
        isPlaceholder: Boolean(userData.isPlaceholder),
    };
};

/**
 * @param {THREE.Group | ProductFacts} selected The dragged product, or its
 * facts when they are already worked out for this drag.
 * @param {THREE.Group} other The product it may snap to.
 * @return {PairFacts} The facts one routing decision is made on.
 */
export const describePair = (
    selected: THREE.Group | ProductFacts,
    other: THREE.Group
): PairFacts => {
    const selectedFacts =
        selected instanceof THREE.Object3D
            ? describeProduct(selected)
            : selected;
    const otherFacts = describeProduct(other);

    return {
        selected: selectedFacts,
        other: otherFacts,
        hasMatchingTopY:
            Math.abs(selectedFacts.box.max.y - otherFacts.box.max.y) < 5,
        sameDirection: selectedFacts.direction === otherFacts.direction,
    };
};

export const computeCabinetSnapMeta = (
    input: ComputeCabinetSnapMetaInput
): ComputeCabinetSnapMetaResult => {
    const {cabinetGroup, otherProduct, snappedProduct} = input;

    // Placeholders (and some panels) may have partial/missing values —
    // optional-chain thickness fields so snap math does not throw.
    const selectedValues = cabinetGroup.userData?.values as
        RoomPlannerProduct | undefined;
    const otherValues = otherProduct.userData?.values as
        RoomPlannerProduct | undefined;

    const exteriorThickness =
        parseInt(selectedValues?.cabinet_ext_thickness?.toString()) || 0;
    const otherProductExtThickness =
        parseInt(otherValues?.cabinet_ext_thickness?.toString()) || 0;
    const carcaseThickness =
        parseInt(selectedValues?.cabinet_carc_thickness?.toString()) || 0;
    const otherCabinetDepth =
        Number(otherProduct.userData.depth) || snappedProduct.depth;
    const otherCabinetWidth =
        Number(otherProduct.userData.width) || snappedProduct.width;
    const kickerFrameEdge = cabinetGroup.userData.kickerFrameEdge as Faces;
    const otherProductKickerFrameEdge = otherProduct.userData
        .kickerFrameEdge as Faces;

    return {
        exteriorThickness,
        otherProductExtThickness,
        carcaseThickness,
        otherCabinetDepth,
        otherCabinetWidth,
        kickerFrameEdge,
        otherProductKickerFrameEdge,
    };
};

export const computeSnapContext = (
    input: ComputeSnapContextInput
): ComputeSnapContextResult => {
    const {
        cabinetGroup,
        otherProduct,
        cabinetRotations,
        isOtherCornerAndLShapedCabinet,
        isKickLength,
    } = input;

    let isBackToBack = false;
    let isFacingRightAndSnapToLShaped = false;
    let isFacingLeftAndSnapToRightReturn = false;
    const backToBackData = {isFrontAndBack: false};
    const allowDirections: SnapDirValue[] = [SnapDir.Right, SnapDir.Left];

    const isUpperSnappingToTall =
        (Boolean(cabinetGroup.userData.isUpperProduct) &&
            !Boolean(otherProduct.userData.isUpperProduct)) ||
        (!Boolean(cabinetGroup.userData.isUpperProduct) &&
            Boolean(otherProduct.userData.isUpperProduct));
    const isUpperSnappingToAnotherUpper =
        Boolean(cabinetGroup.userData.isUpperProduct) &&
        Boolean(otherProduct.userData.isUpperProduct) &&
        !Boolean(otherProduct.userData.isBulkheadComponent);
    const shouldSnapToRightReturnProduct =
        Boolean(otherProduct.userData.isRightReturnProduct) &&
        cabinetRotations.other.isRight &&
        cabinetRotations.selected.isNormal;
    // Parallel side-snap when either cabinet is a return/blind and both face
    // left or both face right (covers dragging the return as well as onto it)
    const isSelectedReturn = Boolean(
        cabinetGroup.userData.isReturnProduct ||
        cabinetGroup.userData.isRightReturnProduct
    );
    const isOtherReturn = Boolean(
        otherProduct.userData.isReturnProduct ||
        otherProduct.userData.isRightReturnProduct
    );
    const shouldSnapParallelReturnSides =
        (isSelectedReturn || isOtherReturn) &&
        ((cabinetRotations.selected.isRight &&
            cabinetRotations.other.isRight) ||
            (cabinetRotations.selected.isLeft &&
                cabinetRotations.other.isLeft));

    if (
        (cabinetRotations.selected.isBack && cabinetRotations.other.isNormal) ||
        (cabinetRotations.other.isBack && cabinetRotations.selected.isNormal)
    ) {
        isBackToBack = true;
        backToBackData.isFrontAndBack = true;
    }
    if (
        (cabinetRotations.selected.isLeft && cabinetRotations.other.isRight) ||
        (cabinetRotations.other.isLeft && cabinetRotations.selected.isRight)
    ) {
        isBackToBack = true;
        backToBackData.isFrontAndBack = false;
    }
    if (
        (cabinetRotations.selected.isRight ||
            cabinetRotations.other.isNormal) &&
        (isOtherCornerAndLShapedCabinet || isOtherReturn) &&
        !shouldSnapParallelReturnSides
    ) {
        isFacingRightAndSnapToLShaped = true;
    }
    if (
        cabinetRotations.selected.isLeft &&
        cabinetRotations.other.isNormal &&
        Boolean(otherProduct.userData.isRightReturnProduct)
    ) {
        isFacingLeftAndSnapToRightReturn = true;
    }

    const snappedProduct = getGroupWidth(otherProduct);
    const isPanelProduct =
        (cabinetGroup.userData.isPanelProduct as boolean) ||
        (cabinetGroup.userData.isPanelProductWithWidth as boolean);
    const isPanelProductOther =
        (otherProduct.userData.isPanelProduct as boolean) ||
        (otherProduct.userData.isPanelProductWithWidth as boolean);
    const isFillerProduct = cabinetGroup.userData.isFillerProduct as boolean;
    const isFloorProduct = cabinetGroup.userData.isFloorProduct as boolean;
    const isBulkheadSnapping =
        cabinetGroup.userData.isBulkheadComponent &&
        !otherProduct.userData.isBulkheadComponent;
    // Manual panels should not be treated as floor products for snapping purposes
    const isFloorProductSnapping = isFloorProduct && !isKickLength;

    return {
        isBackToBack,
        backToBackData,
        isFacingRightAndSnapToLShaped,
        isFacingLeftAndSnapToRightReturn,
        allowDirections,
        isUpperSnappingToTall,
        isUpperSnappingToAnotherUpper,
        shouldSnapToRightReturnProduct,
        shouldSnapParallelReturnSides,
        snappedProduct,
        isPanelProduct,
        isPanelProductOther,
        isFillerProduct,
        isFloorProduct,
        isBulkheadSnapping,
        isFloorProductSnapping,
    };
};
